import { AnyAction } from '@reduxjs/toolkit';
import { combineEpics, StateObservable } from 'redux-observable';
import {
  catchError,
  concatMap,
  filter,
  interval,
  map,
  mapTo,
  merge,
  mergeMap,
  Observable,
  of,
  switchMap,
  takeUntil
} from 'rxjs';
import { SmoLanguage } from '../../../../../Models/Smo/SmoLanguage';
import { allSmoMoonTypes, SmoMoonType } from '../../../../../Models/Smo/SmoMoonType';
import { DEFAULT_GAME_LANGUAGE } from '../../../../../Utils/Constants';
import { radians } from '../../../../../Utils/Trigonometry';
import { Optional } from '../../../../../Utils/Types';
import { ImageCropper } from '../../../../ImageCropper/ImageCropper';
import { ImageFilterer } from '../../../../ImageFilterer/ImageFilterer';
import { TextProcessingFrame } from '../../../../TextProcessing/Models/TextProcessingFrame';
import { TextReader } from '../../../../TextReader/TextReader';
import DebugActions from '../../Debug/DebugActions';
import { StoreDependencies } from '../../StoreDependencies';
import { StoreState } from '../../StoreState';
import VideoAnalysisActions from '../VideoAnalysisActions';

function analyseVideo(
  $action: Observable<AnyAction>,
  _$state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(VideoAnalysisActions.startVideoAnalysis.match),
    map(({ payload }) => payload),
    switchMap((video) =>
      merge(
        // interval(3000).pipe(
        //   takeUntil($action.pipe(filter(VideoAnalysisActions.stopVideoAnalysis.match))),
        //   mapTo(video),
        //   concatMap((video) => dependencies.imageCropper!.getImageFromVideo(video)),
        //   map(VideoAnalysisActions.predictEnvironment)
        // ),
        interval(1000).pipe(
          takeUntil($action.pipe(filter(VideoAnalysisActions.stopVideoAnalysis.match))),
          mapTo(video),
          concatMap((video) => dependencies.imageCropper!.getImageFromVideo(video)),
          mergeMap((image) => [
            ...allSmoMoonTypes.map((moonType) => VideoAnalysisActions.readMoonTitle({ image, moonType })),
            DebugActions.processSingleMoonImage(image),
            DebugActions.processMultiMoonImage(image)
          ])
        )
      )
    )
  );
}

export function processImage(
  image: HTMLImageElement,
  textFrame: TextProcessingFrame,
  imageCropper: ImageCropper,
  imageFilterer: ImageFilterer
): Observable<HTMLImageElement> {
  return imageCropper.rotateImage(image, radians(textFrame.rotation)).pipe(
    concatMap((rotatedImage) => imageCropper.cropImage(rotatedImage, textFrame.crop)),
    concatMap((croppedImage) => imageFilterer.applyFilters(croppedImage, textFrame.filters)),
    catchError(() => processImage(image, textFrame, imageCropper, imageFilterer))
  );
}

export function readImageText(
  image: HTMLImageElement,
  textFrame: TextProcessingFrame,
  imageCropper: ImageCropper,
  imageFilterer: ImageFilterer,
  textReader: TextReader
): Observable<string> {
  return processImage(image, textFrame, imageCropper, imageFilterer).pipe(
    mergeMap((filteredImage) => textReader.readImageText(filteredImage))
  );
}

export function getFilterBrightness(
  moonType: SmoMoonType,
  $state: StateObservable<StoreState>
): Observable<Optional<number>> {
  return of($state.value).pipe(map((state) => state.videoAnalysis.filterBrightness[moonType].value));
}

export function getUserSmoLanguage($state: StateObservable<StoreState>): SmoLanguage {
  return $state.value.user.currentUser?.settings.smoLanguage ?? DEFAULT_GAME_LANGUAGE;
}

export default combineEpics(analyseVideo);
